export interface GradientStop {
  id: string;
  position: number;
  color: { r: number; g: number; b: number };
}

export interface OpacityStop {
  id: string;
  position: number;
  opacity: number;
}

export interface GradientPreset {
  name: string;
  stops: GradientStop[];
  opacityStops: OpacityStop[];
  type: "solid" | "noise";
  smoothness: number;
}

export class GradientEditor {
  private container: HTMLElement;
  private previewCanvas!: HTMLCanvasElement;
  private stopsContainer!: HTMLElement;
  private opacityStopsContainer!: HTMLElement;
  private gradient: {
    stops: GradientStop[];
    opacityStops: OpacityStop[];
    type: "solid" | "noise";
    smoothness: number;
  };
  private presets: GradientPreset[] = [];
  private onChange: ((gradient: typeof this.gradient) => void) | null = null;

  constructor() {
    this.gradient = {
      stops: [
        { id: crypto.randomUUID(), position: 0, color: { r: 0, g: 0, b: 0 } },
        { id: crypto.randomUUID(), position: 100, color: { r: 255, g: 255, b: 255 } },
      ],
      opacityStops: [
        { id: crypto.randomUUID(), position: 0, opacity: 1 },
        { id: crypto.randomUUID(), position: 100, opacity: 1 },
      ],
      type: "solid",
      smoothness: 100,
    };
    this.container = document.createElement("div");
    this.container.className = "gradient-editor";
    // ARIA: dialog region accessibility (#47)
    this.container.setAttribute("role", "dialog");
    this.container.setAttribute("aria-modal", "true");
    this.container.setAttribute("aria-labelledby", "gradient-editor-title");
    // Presets must be initialized BEFORE _buildUI() so the dropdown picks
    // them up in _populatePresets() (called from _buildUI()).
    this._initPresets();
    this._buildUI();
    this._renderPreview();
  }

  private _initPresets(): void {
    this.presets = [
      {
        name: "Foreground to Background",
        stops: [
          { id: crypto.randomUUID(), position: 0, color: { r: 0, g: 0, b: 0 } },
          { id: crypto.randomUUID(), position: 100, color: { r: 255, g: 255, b: 255 } },
        ],
        opacityStops: [
          { id: crypto.randomUUID(), position: 0, opacity: 1 },
          { id: crypto.randomUUID(), position: 100, opacity: 1 },
        ],
        type: "solid",
        smoothness: 100,
      },
      {
        name: "Foreground to Transparent",
        stops: [
          { id: crypto.randomUUID(), position: 0, color: { r: 0, g: 0, b: 0 } },
        ],
        opacityStops: [
          { id: crypto.randomUUID(), position: 0, opacity: 1 },
          { id: crypto.randomUUID(), position: 100, opacity: 0 },
        ],
        type: "solid",
        smoothness: 100,
      },
      {
        name: "Rainbow",
        stops: [
          { id: crypto.randomUUID(), position: 0, color: { r: 255, g: 0, b: 0 } },
          { id: crypto.randomUUID(), position: 20, color: { r: 255, g: 165, b: 0 } },
          { id: crypto.randomUUID(), position: 40, color: { r: 255, g: 255, b: 0 } },
          { id: crypto.randomUUID(), position: 60, color: { r: 0, g: 255, b: 0 } },
          { id: crypto.randomUUID(), position: 80, color: { r: 0, g: 0, b: 255 } },
          { id: crypto.randomUUID(), position: 100, color: { r: 128, g: 0, b: 255 } },
        ],
        opacityStops: [
          { id: crypto.randomUUID(), position: 0, opacity: 1 },
          { id: crypto.randomUUID(), position: 100, opacity: 1 },
        ],
        type: "solid",
        smoothness: 100,
      },
      {
        name: "Spectrum",
        stops: [
          { id: crypto.randomUUID(), position: 0, color: { r: 128, g: 0, b: 255 } },
          { id: crypto.randomUUID(), position: 16, color: { r: 0, g: 0, b: 255 } },
          { id: crypto.randomUUID(), position: 33, color: { r: 0, g: 255, b: 255 } },
          { id: crypto.randomUUID(), position: 50, color: { r: 0, g: 255, b: 0 } },
          { id: crypto.randomUUID(), position: 66, color: { r: 255, g: 255, b: 0 } },
          { id: crypto.randomUUID(), position: 83, color: { r: 255, g: 0, b: 0 } },
          { id: crypto.randomUUID(), position: 100, color: { r: 128, g: 0, b: 255 } },
        ],
        opacityStops: [
          { id: crypto.randomUUID(), position: 0, opacity: 1 },
          { id: crypto.randomUUID(), position: 100, opacity: 1 },
        ],
        type: "solid",
        smoothness: 100,
      },
      {
        name: "Metallic",
        stops: [
          { id: crypto.randomUUID(), position: 0, color: { r: 180, g: 180, b: 180 } },
          { id: crypto.randomUUID(), position: 25, color: { r: 255, g: 255, b: 255 } },
          { id: crypto.randomUUID(), position: 50, color: { r: 100, g: 100, b: 100 } },
          { id: crypto.randomUUID(), position: 75, color: { r: 255, g: 255, b: 255 } },
          { id: crypto.randomUUID(), position: 100, color: { r: 180, g: 180, b: 180 } },
        ],
        opacityStops: [
          { id: crypto.randomUUID(), position: 0, opacity: 1 },
          { id: crypto.randomUUID(), position: 100, opacity: 1 },
        ],
        type: "solid",
        smoothness: 100,
      },
      {
        name: "Pastel",
        stops: [
          { id: crypto.randomUUID(), position: 0, color: { r: 255, g: 182, b: 193 } },
          { id: crypto.randomUUID(), position: 50, color: { r: 173, g: 216, b: 230 } },
          { id: crypto.randomUUID(), position: 100, color: { r: 221, g: 255, b: 173 } },
        ],
        opacityStops: [
          { id: crypto.randomUUID(), position: 0, opacity: 1 },
          { id: crypto.randomUUID(), position: 100, opacity: 1 },
        ],
        type: "solid",
        smoothness: 100,
      },
      {
        name: "Black & White",
        stops: [
          { id: crypto.randomUUID(), position: 0, color: { r: 0, g: 0, b: 0 } },
          { id: crypto.randomUUID(), position: 100, color: { r: 255, g: 255, b: 255 } },
        ],
        opacityStops: [
          { id: crypto.randomUUID(), position: 0, opacity: 1 },
          { id: crypto.randomUUID(), position: 100, opacity: 1 },
        ],
        type: "solid",
        smoothness: 100,
      },
      {
        name: "Red Green Blue",
        stops: [
          { id: crypto.randomUUID(), position: 0, color: { r: 255, g: 0, b: 0 } },
          { id: crypto.randomUUID(), position: 50, color: { r: 0, g: 255, b: 0 } },
          { id: crypto.randomUUID(), position: 100, color: { r: 0, g: 0, b: 255 } },
        ],
        opacityStops: [
          { id: crypto.randomUUID(), position: 0, opacity: 1 },
          { id: crypto.randomUUID(), position: 100, opacity: 1 },
        ],
        type: "solid",
        smoothness: 100,
      },
      {
        name: "Transparent Stripes",
        stops: [
          { id: crypto.randomUUID(), position: 0, color: { r: 0, g: 0, b: 0 } },
          { id: crypto.randomUUID(), position: 100, color: { r: 0, g: 0, b: 0 } },
        ],
        opacityStops: [
          { id: crypto.randomUUID(), position: 0, opacity: 1 },
          { id: crypto.randomUUID(), position: 25, opacity: 0 },
          { id: crypto.randomUUID(), position: 50, opacity: 1 },
          { id: crypto.randomUUID(), position: 75, opacity: 0 },
          { id: crypto.randomUUID(), position: 100, opacity: 1 },
        ],
        type: "solid",
        smoothness: 100,
      },
    ];
  }

  /**
   * Export the current editor state to the GradientStopSet shape used by
   * the core GradientTool (positions normalized to 0..1).
   */
  exportStopSet(): { colorStops: Array<{ position: number; color: { r: number; g: number; b: number } }>; opacityStops: Array<{ position: number; opacity: number }> } {
    return {
      colorStops: this.gradient.stops
        .slice()
        .sort((a, b) => a.position - b.position)
        .map(s => ({ position: s.position / 100, color: { ...s.color } })),
      opacityStops: this.gradient.opacityStops
        .slice()
        .sort((a, b) => a.position - b.position)
        .map(s => ({ position: s.position / 100, opacity: s.opacity })),
    };
  }

  /** Programmatically add a color stop at the given position (0..100). For tests. */
  addColorStopAt(position: number, color: { r: number; g: number; b: number } = { r: 128, g: 128, b: 128 }): void {
    this.gradient.stops.push({ id: crypto.randomUUID(), position, color });
    this.gradient.stops.sort((a, b) => a.position - b.position);
    this._renderStops();
    this._renderPreview();
    this._emitChange();
  }

  /** Programmatically add an opacity stop at the given position (0..100). For tests. */
  addOpacityStopAt(position: number, opacity: number = 1): void {
    this.gradient.opacityStops.push({ id: crypto.randomUUID(), position, opacity });
    this.gradient.opacityStops.sort((a, b) => a.position - b.position);
    this._renderStops();
    this._renderPreview();
    this._emitChange();
  }

  /** Returns the list of preset names — for tests. */
  getPresetNames(): string[] {
    return this.presets.map(p => p.name);
  }

  /** Returns the underlying container element — for tests and host wiring. */
  getElement(): HTMLElement {
    return this.container;
  }

  private _buildUI(): void {
    this.container.innerHTML = `
      <div class="panel-title" id="gradient-editor-title">Gradient Editor</div>
      <div class="gradient-editor__content">
        <div class="gradient-presets">
          <select id="gradient-preset-select">
            <option value="">— Presets —</option>
          </select>
        </div>
        <div class="gradient-preview">
          <canvas id="gradient-preview-canvas" width="256" height="20"></canvas>
        </div>
        <div class="gradient-stops-container" id="gradient-stops-container"></div>
        <div class="gradient-opacity-stops-container" id="gradient-opacity-stops-container"></div>
        <div class="gradient-controls">
          <div class="brush-row">
            <label>Smoothness</label>
            <input type="range" min="0" max="100" value="100" id="gradient-smoothness" class="brush-slider">
            <span class="brush-val" id="gradient-smoothness-val">100%</span>
          </div>
          <div class="brush-row">
            <label>Type</label>
            <select id="gradient-type">
              <option value="solid">Solid</option>
              <option value="noise">Noise</option>
            </select>
          </div>
          <div class="gradient-stop-controls" id="gradient-stop-controls" style="display:none">
            <div class="brush-row">
              <label>Position</label>
              <input type="number" min="0" max="100" value="0" id="gradient-stop-position" style="width:50px">
            </div>
            <div class="brush-row" id="gradient-color-row">
              <label>Color</label>
              <input type="color" id="gradient-stop-color" value="#000000" style="width:40px;height:20px">
            </div>
            <div class="brush-row" id="gradient-opacity-row" style="display:none">
              <label>Opacity</label>
              <input type="range" min="0" max="100" value="100" id="gradient-stop-opacity" class="brush-slider">
            </div>
            <button class="btn-sm" id="gradient-stop-delete" style="margin-top:4px">Delete Stop</button>
          </div>
        </div>
      </div>
    `;

    this.previewCanvas = this.container.querySelector("#gradient-preview-canvas")!;
    this.stopsContainer = this.container.querySelector("#gradient-stops-container")!;
    this.opacityStopsContainer = this.container.querySelector("#gradient-opacity-stops-container")!;

    this._bindEvents();
    this._populatePresets();
    this._renderStops();
  }

  private _populatePresets(): void {
    const select = this.container.querySelector("#gradient-preset-select") as HTMLSelectElement;
    if (!select) return;
    for (const preset of this.presets) {
      const opt = document.createElement("option");
      opt.value = preset.name;
      opt.textContent = preset.name;
      select.appendChild(opt);
    }
  }

  private _bindEvents(): void {
    const smoothnessSlider = this.container.querySelector("#gradient-smoothness") as HTMLInputElement;
    smoothnessSlider?.addEventListener("input", () => {
      this.gradient.smoothness = parseInt(smoothnessSlider.value);
      this.container.querySelector("#gradient-smoothness-val")!.textContent = smoothnessSlider.value + "%";
      this._renderPreview();
      this._emitChange();
    });

    const typeSelect = this.container.querySelector("#gradient-type") as HTMLSelectElement;
    typeSelect?.addEventListener("change", () => {
      this.gradient.type = typeSelect.value as "solid" | "noise";
      this._renderPreview();
      this._emitChange();
    });

    const presetSelect = this.container.querySelector("#gradient-preset-select") as HTMLSelectElement;
    presetSelect?.addEventListener("change", () => {
      const preset = this.presets.find(p => p.name === presetSelect.value);
      if (preset) {
        this.gradient = {
          stops: preset.stops.map(s => ({ ...s })),
          opacityStops: preset.opacityStops.map(s => ({ ...s })),
          type: preset.type,
          smoothness: preset.smoothness,
        };
        this._renderStops();
        this._renderPreview();
        this._emitChange();
      }
    });

    const deleteBtn = this.container.querySelector("#gradient-stop-delete");
    deleteBtn?.addEventListener("click", () => this._deleteSelectedStop());

    const stopPos = this.container.querySelector("#gradient-stop-position") as HTMLInputElement;
    stopPos?.addEventListener("change", () => {
      this._updateSelectedStop("position", parseInt(stopPos.value));
    });

    const stopColor = this.container.querySelector("#gradient-stop-color") as HTMLInputElement;
    stopColor?.addEventListener("change", () => {
      const hex = stopColor.value;
      const r = parseInt(hex.slice(1, 3), 16);
      const g = parseInt(hex.slice(3, 5), 16);
      const b = parseInt(hex.slice(5, 7), 16);
      this._updateSelectedStop("color", { r, g, b });
    });

    const stopOpacity = this.container.querySelector("#gradient-stop-opacity") as HTMLInputElement;
    stopOpacity?.addEventListener("input", () => {
      this._updateSelectedStop("opacity", parseInt(stopOpacity.value) / 100);
    });
  }

  private _selectedStopId: string | null = null;
  private _selectedStopType: "color" | "opacity" = "color";

  private _renderStops(): void {
    // Color stops
    this.stopsContainer.innerHTML = "";
    this.stopsContainer.style.cssText = "position:relative;height:16px;margin:4px 0;";
    for (const stop of this.gradient.stops) {
      const el = document.createElement("div");
      el.style.cssText = `position:absolute;bottom:0;width:10px;height:10px;background:rgb(${stop.color.r},${stop.color.g},${stop.color.b});border:1px solid #888;cursor:pointer;left:${stop.position}%;transform:translateX(-50%);`;
      el.dataset.id = stop.id;
      el.dataset.type = "color";
      el.addEventListener("click", (e) => {
        e.stopPropagation();
        this._selectStop(stop.id, "color");
      });
      el.addEventListener("dblclick", (e) => {
        e.stopPropagation();
        this._selectStop(stop.id, "color");
      });
      this.stopsContainer.appendChild(el);
    }
    this.stopsContainer.addEventListener("click", (e) => {
      if (e.target === this.stopsContainer) {
        const rect = this.stopsContainer.getBoundingClientRect();
        const pos = Math.round(((e.clientX - rect.left) / rect.width) * 100);
        this._addColorStop(pos);
      }
    });

    // Opacity stops
    this.opacityStopsContainer.innerHTML = "";
    this.opacityStopsContainer.style.cssText = "position:relative;height:16px;margin:4px 0;";
    for (const stop of this.gradient.opacityStops) {
      const el = document.createElement("div");
      const gray = Math.round(stop.opacity * 255);
      el.style.cssText = `position:absolute;bottom:0;width:10px;height:10px;background:rgb(${gray},${gray},${gray});border:1px solid #888;cursor:pointer;left:${stop.position}%;transform:translateX(-50%);`;
      el.dataset.id = stop.id;
      el.dataset.type = "opacity";
      el.addEventListener("click", (e) => {
        e.stopPropagation();
        this._selectStop(stop.id, "opacity");
      });
      this.opacityStopsContainer.appendChild(el);
    }
    this.opacityStopsContainer.addEventListener("click", (e) => {
      if (e.target === this.opacityStopsContainer) {
        const rect = this.opacityStopsContainer.getBoundingClientRect();
        const pos = Math.round(((e.clientX - rect.left) / rect.width) * 100);
        this._addOpacityStop(pos);
      }
    });
  }

  private _selectStop(id: string, type: "color" | "opacity"): void {
    this._selectedStopId = id;
    this._selectedStopType = type;
    const controls = this.container.querySelector("#gradient-stop-controls") as HTMLElement;
    controls.style.display = "";

    if (type === "color") {
      const stop = this.gradient.stops.find(s => s.id === id);
      if (stop) {
        (this.container.querySelector("#gradient-stop-position") as HTMLInputElement).value = String(stop.position);
        const hex = `#${stop.color.r.toString(16).padStart(2, "0")}${stop.color.g.toString(16).padStart(2, "0")}${stop.color.b.toString(16).padStart(2, "0")}`;
        (this.container.querySelector("#gradient-stop-color") as HTMLInputElement).value = hex;
        (this.container.querySelector("#gradient-color-row") as HTMLElement).style.display = "";
        (this.container.querySelector("#gradient-opacity-row") as HTMLElement).style.display = "none";
      }
    } else {
      const stop = this.gradient.opacityStops.find(s => s.id === id);
      if (stop) {
        (this.container.querySelector("#gradient-stop-position") as HTMLInputElement).value = String(stop.position);
        (this.container.querySelector("#gradient-stop-opacity") as HTMLInputElement).value = String(Math.round(stop.opacity * 100));
        (this.container.querySelector("#gradient-color-row") as HTMLElement).style.display = "none";
        (this.container.querySelector("#gradient-opacity-row") as HTMLElement).style.display = "";
      }
    }
  }

  private _addColorStop(position: number): void {
    const color = this.gradient.stops.length > 0 ? this.gradient.stops[this.gradient.stops.length - 1]!.color : { r: 128, g: 128, b: 128 };
    this.gradient.stops.push({ id: crypto.randomUUID(), position, color });
    this.gradient.stops.sort((a, b) => a.position - b.position);
    this._renderStops();
    this._renderPreview();
    this._emitChange();
  }

  private _addOpacityStop(position: number): void {
    this.gradient.opacityStops.push({ id: crypto.randomUUID(), position, opacity: 1 });
    this.gradient.opacityStops.sort((a, b) => a.position - b.position);
    this._renderStops();
    this._renderPreview();
    this._emitChange();
  }

  private _deleteSelectedStop(): void {
    if (!this._selectedStopId) return;
    if (this._selectedStopType === "color") {
      if (this.gradient.stops.length <= 2) return;
      this.gradient.stops = this.gradient.stops.filter(s => s.id !== this._selectedStopId);
    } else {
      if (this.gradient.opacityStops.length <= 2) return;
      this.gradient.opacityStops = this.gradient.opacityStops.filter(s => s.id !== this._selectedStopId);
    }
    this._selectedStopId = null;
    (this.container.querySelector("#gradient-stop-controls") as HTMLElement).style.display = "none";
    this._renderStops();
    this._renderPreview();
    this._emitChange();
  }

  private _updateSelectedStop(prop: string, value: any): void {
    if (!this._selectedStopId) return;
    if (this._selectedStopType === "color") {
      const stop = this.gradient.stops.find(s => s.id === this._selectedStopId);
      if (stop) {
        (stop as any)[prop] = value;
        this._renderPreview();
        this._emitChange();
      }
    } else {
      const stop = this.gradient.opacityStops.find(s => s.id === this._selectedStopId);
      if (stop) {
        (stop as any)[prop] = value;
        this._renderPreview();
        this._emitChange();
      }
    }
  }

  private _renderPreview(): void {
    const ctx = this.previewCanvas.getContext("2d");
    if (!ctx) return; // jsdom test env returns null
    const w = this.previewCanvas.width;
    const h = this.previewCanvas.height;

    if (this.gradient.type === "noise") {
      const imgData = ctx.createImageData(w, h);
      for (let x = 0; x < w; x++) {
        const t = x / (w - 1);
        const r = Math.random() * 255;
        const g = Math.random() * 255;
        const b = Math.random() * 255;
        for (let y = 0; y < h; y++) {
          const idx = (y * w + x) * 4;
          imgData.data[idx] = r;
          imgData.data[idx + 1] = g;
          imgData.data[idx + 2] = b;
          imgData.data[idx + 3] = 255;
        }
      }
      ctx.putImageData(imgData, 0, 0);
      return;
    }

    const sortedStops = [...this.gradient.stops].sort((a, b) => a.position - b.position);
    const sortedOpacity = [...this.gradient.opacityStops].sort((a, b) => a.position - b.position);

    for (let x = 0; x < w; x++) {
      const t = x / (w - 1) * 100;

      // Find color
      let r = 0, g = 0, b = 0;
      if (sortedStops.length === 1) {
        const s = sortedStops[0]!;
        r = s.color.r; g = s.color.g; b = s.color.b;
      } else {
        for (let i = 0; i < sortedStops.length - 1; i++) {
          if (t >= sortedStops[i]!.position && t <= sortedStops[i + 1]!.position) {
            const s1 = sortedStops[i]!, s2 = sortedStops[i + 1]!;
            const localT = (t - s1.position) / (s2.position - s1.position);
            const smooth = this._smoothstep(localT);
            r = Math.round(s1.color.r + (s2.color.r - s1.color.r) * smooth);
            g = Math.round(s1.color.g + (s2.color.g - s1.color.g) * smooth);
            b = Math.round(s1.color.b + (s2.color.b - s1.color.b) * smooth);
            break;
          }
        }
      }

      // Find opacity
      let a = 255;
      if (sortedOpacity.length === 1) {
        a = Math.round(sortedOpacity[0]!.opacity * 255);
      } else {
        for (let i = 0; i < sortedOpacity.length - 1; i++) {
          if (t >= sortedOpacity[i]!.position && t <= sortedOpacity[i + 1]!.position) {
            const s1 = sortedOpacity[i]!, s2 = sortedOpacity[i + 1]!;
            const localT = (t - s1.position) / (s2.position - s1.position);
            a = Math.round((s1.opacity + (s2.opacity - s1.opacity) * localT) * 255);
            break;
          }
        }
      }

      ctx.fillStyle = `rgba(${r},${g},${b},${a / 255})`;
      ctx.fillRect(x, 0, 1, h);
    }
  }

  private _smoothstep(t: number): number {
    const s = this.gradient.smoothness / 100;
    return t * t * (3 - 2 * t) * s + t * (1 - s);
  }

  private _emitChange(): void {
    if (this.onChange) {
      this.onChange({ ...this.gradient });
    }
  }

  setCallback(onChange: (gradient: typeof this.gradient) => void): void {
    this.onChange = onChange;
  }

  getGradient(): typeof this.gradient {
    return { ...this.gradient };
  }

  setGradient(gradient: Partial<typeof this.gradient>): void {
    Object.assign(this.gradient, gradient);
    this._renderStops();
    this._renderPreview();
    this._emitChange();
  }

  savePreset(name: string): void {
    this.presets.push({
      name,
      stops: this.gradient.stops.map(s => ({ ...s })),
      opacityStops: this.gradient.opacityStops.map(s => ({ ...s })),
      type: this.gradient.type,
      smoothness: this.gradient.smoothness,
    });
    this._populatePresets();
  }

  mount(parent: HTMLElement): void {
    parent.appendChild(this.container);
    this._renderPreview();
  }

  destroy(): void {
    this.container.remove();
  }
}
